import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  AdminPanelSettings, 
  Shield, 
  LockOutlined, 
  Fingerprint as FingerprintIcon 
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import FingerprintAuth from '../components/auth/FingerprintAuth';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fingerprintDialogOpen, setFingerprintDialogOpen] = useState(false);
  const { login } = useAuth();

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }
    
    const success = login(username, password);
    if (!success) {
      setError('Invalid credentials. Try client1/password or admin_user/adminpass');
    }
  };

  const handleDirectLogin = () => {
    setLoading(true);
    setError('');
    
    // Automatically log in as admin
    setTimeout(() => {
      const success = login('admin_user', 'adminpass');
      if (!success) {
        setError('Error logging in automatically. Please try manual login.');
      }
      setLoading(false);
    }, 1000); // Add a small delay to simulate loading
  };
  
  const handleClientLogin = () => {
    // Set default client credentials
    setUsername('client1');
    setPassword('password');
    // Open fingerprint dialog
    setFingerprintDialogOpen(true);
  };
  
  const handleFingerprintSuccess = () => {
    setLoading(true);
    setError('');
    
    // Log in with client credentials
    setTimeout(() => {
      const success = login(username, password);
      if (!success) {
        setError('Error logging in. Please try again.');
      }
      setLoading(false);
      setFingerprintDialogOpen(false);
    }, 500);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'background.default',
        padding: 2
      }}
    >
      <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
        <Card sx={{ overflow: 'visible' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 56,
                  height: 56,
                  borderRadius: '12px',
                  backgroundColor: 'primary.50',
                  mb: 2
                }}
              >
                <LockOutlined sx={{ color: 'primary.600', fontSize: 28 }} />
              </Box>
              <Typography component="h1" variant="h4" align="center" fontWeight="600">
                Log in to SentinelEye
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Enter your credentials to access your dashboard
              </Typography>
            </Box>
            
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
            
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>
                  Username
                </Typography>
                <TextField
                  required
                  fullWidth
                  id="username"
                  name="username"
                  placeholder="Enter your username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    sx: { borderRadius: 2, py: 0.5 }
                  }}
                />
              </Box>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>
                  Password
                </Typography>
                <TextField
                  required
                  fullWidth
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    sx: { borderRadius: 2, py: 0.5 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                sx={{ py: 1.5, borderRadius: 2, mb: 3 }}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                disabled={loading}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
              
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>
              
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AdminPanelSettings />}
                onClick={handleDirectLogin}
                disabled={loading}
                sx={{ py: 1.5, borderRadius: 2, mb: 2 }}
              >
                Go to App (as Admin)
              </Button>
              
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FingerprintIcon />}
                onClick={handleClientLogin}
                disabled={loading}
                sx={{ py: 1.5, borderRadius: 2 }}
              >
                Client Login with Fingerprint
              </Button>
            </Box>
            
            <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 4, display: 'block' }}>
              Login with: Username = client1, password = password<br />
              or Username = admin_user, password = adminpass
            </Typography>
          </CardContent>
        </Card>
      </Container>
      
      {/* Fingerprint Authentication Dialog */}
      <FingerprintAuth
        open={fingerprintDialogOpen}
        onSuccess={handleFingerprintSuccess}
        onCancel={() => setFingerprintDialogOpen(false)}
        title="Authenticate to Login"
      />
    </Box>
  );
};

export default LoginPage;
